import React, { useCallback } from 'react';

import { FormattedMessage, useIntl } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import Button from '@atlaskit/button/new';
import MagicWandIcon from '@atlaskit/icon/core/magic-wand';
import { Flex } from '@atlaskit/primitives/compiled';
import { useProductDependencies } from '@atlassian/company-hub-common/controllers/use-product-dependencies';
import { RangeField } from '@atlassian/company-hub-common/range-field';
import { ToggleField as SharedToggleField } from '@atlassian/company-hub-common/ToggleField';
import { RenderVariant } from '@atlassian/company-hub-common/types';

import { CARDS_EXTENSION_CONFIG_ANALYTIC_SOURCE } from '../../../../../constants';
import type { CardsActions } from '../../../../../controllers/use-cards-extension-parameters-and-actions/types';
import type { CardsExtensionParameters } from '../../../../../types';

import { messages } from './messages';

type DynamicCardsContentProps = {
	extensionActions: CardsActions;
	extensionParameters: CardsExtensionParameters;
	viewFilters: () => void;
};
export const DynamicCardsContent = ({
	viewFilters,
	extensionParameters,
	extensionActions,
}: DynamicCardsContentProps): React.JSX.Element => {
	const { cardCount, isImageShown } = extensionParameters;
	const { updateMainFormField } = extensionActions;
	const { isHelpHub } = useProductDependencies();
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	// Help Hub only: authors can hide the (otherwise force-shown) header image
	// on dynamic cards. Absent/true means shown, preserving existing behaviour.
	const isImageCurrentlyShown = isImageShown !== false;

	const handleImageToggleChange = useCallback(() => {
		const nextIsImageShown = !isImageCurrentlyShown;
		updateMainFormField({ isImageShown: nextIsImageShown });
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'changed',
				actionSubject: 'toggle',
				actionSubjectId: 'imageToggle',
				source: CARDS_EXTENSION_CONFIG_ANALYTIC_SOURCE,
				attributes: {
					value: nextIsImageShown,
					// This toggle only exists in the dynamic variant; mirror the
					// other display-option toggles which segment by renderVariant.
					renderVariant: RenderVariant.DYNAMIC,
				},
			},
		}).fire();
	}, [isImageCurrentlyShown, updateMainFormField, createAnalyticsEvent]);

	return (
		<>
			<Button shouldFitContainer appearance="primary" onClick={viewFilters}>
				<Flex gap="space.100" justifyContent="center">
					<MagicWandIcon label="" />
					<FormattedMessage {...messages.viewCurrentFilters} />
				</Flex>
			</Button>
			<RangeField
				analyticSource={CARDS_EXTENSION_CONFIG_ANALYTIC_SOURCE}
				label={messages.cardCountLabel}
				maxValue={20}
				minValue={1}
				prefix="card"
				updateRangeValue={(value: number) => updateMainFormField({ cardCount: value })}
				value={cardCount}
			/>
			{isHelpHub && (
				<SharedToggleField
					label={formatMessage(messages.showImagesToggleLabel)}
					isChecked={isImageCurrentlyShown}
					onChange={handleImageToggleChange}
				/>
			)}
		</>
	);
};
